import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
const _excluded = ["children", "groupAriaLabel", "header"],
      _excluded2 = ["target", "url", "onClick"];

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import { useTheme } from "@emotion/react";
import PropTypes from "prop-types";
import React, { cloneElement } from "react";
import { LINK, MEGAMENUITEM, MEGAMENUITEMGROUP, NAVLINK } from "../../common/constants";
import { invalidIf, requiredIf } from "../../common/proptypes";
import { OVERRIDE } from "../../themes/common/classes";
import { Anchor, IconForward } from "../..";
import extractTheme from "./extractTheme";
/**
 * MegaMenuItemGroup is a React component that renders a group of focusable
 * elements.
 * MegaMenuItemGroup can only be used as a child of MegaMenu component.
 */

import { jsx as _jsx } from "@emotion/react/jsx-runtime";
import { jsxs as _jsxs } from "@emotion/react/jsx-runtime";

function MegaMenuItemGroup(_ref) {
  let {
    children,
    groupAriaLabel,
    header
  } = _ref,
      otherProps = _objectWithoutProperties(_ref, _excluded);

  const theme = useTheme();
  const {
    megaMenuItems,
    megaMenuGroupLink,
    wrapper,
    icon
  } = extractTheme(_objectSpread({
    theme
  }, otherProps));
  /**
   * This method validates the value of `routeTo` in `header` prop and returns a
   * renderable component.
   * @returns An V4 Anchor component or user passed component(NavLink, Link).
   */

  const renderGroupHeader = () => {
    var _routeTo$type;

    const {
      routeTo,
      disabled,
      label
    } = header;
    const routeToType = typeof routeTo;
    let itemProps = {
      noBox: true,
      buttonStyles: megaMenuGroupLink,
      disabled,
      rightIcon: _jsx(IconForward, {
        theme: "inverse",
        size: "xsmall",
        decorative: true
      })
    };
    const isObjectType = routeToType === "object" && !((_routeTo$type = routeTo.type) !== null && _routeTo$type !== void 0 && _routeTo$type.displayName);

    if (isObjectType) {
      const {
        target = "_self",
        url,
        onClick
      } = routeTo,
            otherRouteToProps = _objectWithoutProperties(routeTo, _excluded2);

      itemProps = _objectSpread(_objectSpread({}, itemProps), {}, {
        target,
        href: url,
        tabIndex: disabled ? -1 : 0,
        onClick: onClick && (e => onClick(e, _objectSpread({
          target,
          url
        }, otherRouteToProps)))
      }, otherRouteToProps);
    } else if (routeToType === "string") {
      itemProps = _objectSpread(_objectSpread({}, itemProps), {}, {
        href: routeTo
      });
    }

    if (routeToType === "string" || isObjectType) {
      return _jsx(Anchor, _objectSpread(_objectSpread({}, itemProps), {}, {
        children: label
      }));
    } else {
      return _jsx("div", {
        css: wrapper,
        children: /*#__PURE__*/cloneElement(routeTo, {
          className: "pnr-base-button pnr-megamenuitemgroup"
        }, [routeTo.props.children, _jsx(IconForward, {
          style: icon,
          size: "xsmall",
          decorative: true
        }, "groupHeaderIcon")])
      });
    }
  };

  return _jsxs("li", {
    className: OVERRIDE,
    children: [header && renderGroupHeader(), _jsx("ul", {
      className: OVERRIDE,
      "aria-label": groupAriaLabel,
      css: megaMenuItems,
      children: children
    })]
  });
}

MegaMenuItemGroup.propTypes = process.env.NODE_ENV !== "production" ? {
  // Aria label for list of clickable links displayed in MegaMenuItemGroup.
  groupAriaLabel: PropTypes.string.isRequired,

  /** This is what will be displayed inside the component. */
  children: invalidIf(PropTypes.node, (props, propName) => {
    let error = false;
    const prop = props[propName];
    React.Children.map(prop, child => {
      const childrenType = child.type;

      if (childrenType.displayName !== MEGAMENUITEM) {
        error = true;
      }
    });
    return error;
  }, `'MegaMenuItemGroup' children should be of type ${MEGAMENUITEM}.`),
  // This will be group heading link.
  header: PropTypes.shape({
    // To disable a group link and prevent any action on it.
    disabled: PropTypes.bool,

    /**
     * Label to displayed on group link.
     * Required if `routeTo` prop is of type string or object.
     */
    label: requiredIf(PropTypes.string, props => !props.label && typeof props.routeTo !== "object", "when 'routeTo' is of type string or object"),

    /**
     * It can be a string to redirect or an object or `NavLink` or `Link`
     * components of react router.
     */
    routeTo: invalidIf(PropTypes.oneOfType([PropTypes.string, PropTypes.element, PropTypes.shape({
      url: requiredIf(PropTypes.string, props => !props.url && !props.onClick, "when `onClick` is not passed"),
      target: PropTypes.oneOf(["_blank", "_self", "_parent", "_top"]),
      onClick: PropTypes.func
    })]).isRequired, (props, propName) => {
      let error = false;
      const prop = props[propName];
      const propType = typeof prop;

      if (propType === "object" && prop) {
        var _prop$type;

        const displayName = (_prop$type = prop.type) === null || _prop$type === void 0 ? void 0 : _prop$type.displayName;

        if ((displayName || prop.type) && displayName !== LINK && displayName !== NAVLINK) {
          error = true;
        }
      }

      return error;
    }, `'routeTo' should be of type 'string' or 'object' or accepts only 'Link' or 'NavLink' as components.`)
  })
} : {};
MegaMenuItemGroup.defaultProps = {
  children: null,
  header: null
};
MegaMenuItemGroup.displayName = MEGAMENUITEMGROUP;
export default MegaMenuItemGroup;